import React from 'react';
import PropTypes from 'prop-types';
import { Input } from 'zarm';
import { cascadeEngine } from '@iyunbao/cr-engine';

import UItem, { setDecoratorItem } from '../item';
import SelectComp from '../select/select';
import { deepAsset, clearObj, getFormId } from '../../utils/tools';
import { BaseWidgetProps, OptionsProps } from '../../utils/type';
import ImageOCR from '../input/imageOCR';

export interface SelectProps extends BaseWidgetProps {
  label: any;
  value: any;
  defaultValue: any;
  placeholder?: string;
  options: OptionsProps;
  valueMember?: string;
  readonly?: boolean;
  readOnly?: boolean;
  maxLength?: number | string;
  disabled?: boolean;
  refreshPrice?: boolean;
  className?: string;
  name?: string;
  dataBind?: string;
  imageOCR?: 'idcard';
  onChange?: (value: any) => void;
  validate?: {
    rules: any[];
  };
}

const getState = (props: SelectProps, values?: any) => {
  const { onChange, defaultValue, value, label, _crengine = {}, ...others } = props;

  let stateValue = props.value || {};

  if (values) {
    stateValue = values;
  } else if (!(value && Object.keys(value).length > 1) && typeof defaultValue !== 'undefined') {
    stateValue = defaultValue;
  }

  const state: any = {
    defaultValue: stateValue,
  };

  // 联动
  if (label && label.cascade) {
    const { dataBind, name, ...rest } = label;
    let labelValue = '';

    if (stateValue && dataBind) {
      labelValue = (stateValue[dataBind] || '');
    }

    const itemProps: any = {
      widget: 'select',
      value: labelValue,
      wprops: rest,
    };

    const dataProps: any = {
      dataBind: _crengine.dataBind,
      name: _crengine.name,
      wprops: others,
    };

    const cascadeData = cascadeEngine.cascadeChange(itemProps, dataProps);

    if (cascadeData && cascadeData.wprops && cascadeData.wprops.validate) {
      state.validate = cascadeData.wprops.validate;
    }
  }

  return state;
};

const parseDataBind = (crengine: any, dataBind: string) => {
  if (crengine && crengine.dataBind) {
    return `${crengine.dataBind}.${dataBind}`;
  }

  return dataBind;
};

interface SelectState {
  defaultValue: any;
  prevValue?: any;
  validate?: any;
}

class SelectCert extends React.PureComponent<SelectProps, SelectState> {
  static propTypes = {
    onChange: PropTypes.func,
  };

  static defaultProps = {
    onChange: () => {},
  };

  state: SelectState = {
    ...getState(this.props),
  };

  timer: number | null = null;

  static getDerivedStateFromProps(nextProps: SelectProps, state: SelectState) {
    if ('value' in nextProps && !deepAsset(nextProps.value, state.prevValue)) {
      return {
        ...getState(nextProps),
        prevValue: nextProps.value,
      };
    }

    return null;
  }

  onChange(value: string, key: string, type?: string) {
    const { defaultValue } = this.state;
    const { onChange, ...others } = this.props;
    const { label, dataBind: inputDataBind, _crengine } = this.props;
    const { form, dataBind, path } = _crengine || {};
    const name = getFormId(dataBind, path);

    if (key === 'certNo') {
      value = value.toUpperCase();
    }

    defaultValue[key] = value;
    others.value = defaultValue;

    let newState: any = {
      defaultValue
    };

    // 重置 select form value
    if (type && form && key) {
      form.setFieldsValue({
        [`${name}.${key}`]: value
      });
    }

    // 特殊证件类型
    if (type === 'type' && label && label.cascade) {
      newState = {
        ...getState(this.props, defaultValue),
        defaultValue,
      };

      // 证件类型，触发onChange 验证
      if (
        form
        && inputDataBind
        && defaultValue[inputDataBind]
      ) {
        const names = `${name}.${inputDataBind}`;
        form.resetFields(names);

        this.timer && clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          form.validateFields([names]);
        }, 300);
      }
    }

    this.setState(newState);

    if (onChange) {
      onChange(others);
    }
  }

  // 证件类型是否身份证
  isIdcard = () => {
    const { defaultValue } = this.state;
    const { label } = this.props;
    let idcard = false;

    if (
      label
      && label.options
      && Array.isArray(label.options)
      && label.options.length
      && defaultValue
    ) {
      const { dataBind = 'certType', options } = label;
      const value = defaultValue[dataBind] || '';

      const len = options.length;
      let item: any = {};

      for (let i = 0; i < len; i += 1) {
        item = options[i];

        if (item && item.value === value) {
          idcard = true;
          break;
        }
      }
    }

    return idcard;
  };

  renderSelect() {
    const { defaultValue } = this.state;
    const {
      label = {},
      valueMember = 'value',
      _crengine = {},
    } = this.props;

    // label select
    if (label) {
      const { form, path } = _crengine;
      const {
        name,
        dataBind = 'certType',
        options,
        cascade,
        readOnly,
        ...labelRest
      } = label;

      const selectProps: any = clearObj({
        ...labelRest,
        valueMember,
        value: defaultValue[dataBind],
        dataSource: options,
        readOnly,
        onChange: (selected) => this.onChange(selected, dataBind, 'type'),
      });
      const validatorProps = clearObj({
        path,
        form,
        dataBind: parseDataBind(_crengine, dataBind),
        initialValue: defaultValue[dataBind],
        onChange: (selected) => this.onChange(selected, dataBind, 'type'),
      });

      return (
        <>
          {setDecoratorItem(validatorProps, <input type="hidden" />)}
          <SelectComp
            {...selectProps}
          />
        </>
      );
    }

    return '';
  }

  render() {
    const { defaultValue: stateDefaultValue, validate } = this.state;
    const {
      label,
      dataBind = 'certNo',
      value,
      disabled,
      defaultValue,
      maxLength,
      placeholder,
      readOnly,
      readonly,
      imageOCR,
      _crengine = {},
      onChange,
      onLog,
      ...rest
    } = this.props;
    const { form } = _crengine || {};
    const newReadOnly = (readOnly || readonly);

    const inputProps = clearObj({
      defaultValue: (stateDefaultValue[dataBind] || ''),
      disabled,
      maxLength,
      placeholder,
      readOnly: newReadOnly,
      onBlur: (v: string) => this.onChange(v, dataBind)
    });
    // replace dataBind input crengine
    const inputCrengine = {
      ..._crengine,
      dataBind: parseDataBind(_crengine, dataBind),
    };

    // 如果wform存在 则去除input组件的某些属性
    if (form) {
      delete inputProps.onBlur;
      delete inputProps.defaultValue;
    }

    const attachProps: any = {};

    // OCR接口只支持身份证，其他证件类型隐藏按钮
    if (!newReadOnly && imageOCR === 'idcard' && this.isIdcard()) {
      attachProps.description = (
        <ImageOCR
          type={imageOCR}
          onChange={(v) => this.onChange(v, dataBind, 'orc')}
          onLog={onLog}
        />
      );
    }

    return (
      <UItem
        {...rest}
        {...attachProps}
        validate={validate}
        itemType="selectCert"
        label={this.renderSelect()}
        initialValue={stateDefaultValue[dataBind] || ''}
        _crengine={inputCrengine}
        validateTrigger="onBlur"
        onBlur={(v) => this.onChange(v, dataBind)}
      >
        <Input {...inputProps} />
      </UItem>
    );
  }
}

export default SelectCert;
